import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigurationService } from '@aodocs/configuration-service';
import { InternationalizationService } from '@aodocs/shared-translation';
import { concatMap, map, Observable } from 'rxjs';

import { QueryParamRetreiverUtils } from './utils/query-params-retreiver.utils';

const addAuthenticationHeader = (
  tokenGenerator: () => Observable<string | null>,
  request: HttpRequest<unknown>
): Observable<HttpRequest<unknown>> =>
  tokenGenerator().pipe(
    map((token) => {
      if (token) {
        return request.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
        });
      }
      return request;
    })
  );

export const addDomainAndLocaleInterceptor: (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  intercepted?: string[],
  router?: Router,
  window?: Window,
  i18nService?: InternationalizationService
) => Observable<HttpEvent<unknown>> = (
  request,
  next,
  intercepted = [inject(ConfigurationService).configuration.aodocsApiUrl],
  router = inject(Router),
  window = inject(Window),
  i18nService = inject(InternationalizationService)
) => {
  const localeParam = i18nService.currentLocale;
  const regexp = /\/domain\/([^/]*)\//;
  let domainParam = regexp.exec(window.location.href)?.[1];
  if (!domainParam) {
    // On login page, we do not have domain in the path, it is still available in the query params
    domainParam = QueryParamRetreiverUtils.getQueryParamFromRouter('aodocs-domain', router);
  }

  const isInterceptedUrl = intercepted.some((url) => request.url.includes(url));
  if (isInterceptedUrl) {
    const newRequest = request.clone({
      setParams: {
        locale: localeParam.replace('-', '_'),
        domain: domainParam || '',
      },
    });
    return next(newRequest);
  }
  return next(request);
};

export const defaultInterceptor: (
  intercepted: string[],
  tokenGenerator: () => Observable<string | null>
) => (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn) => Observable<HttpEvent<unknown>> =
  (intercepted: string[], tokenGenerator: () => Observable<string | null>) => (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn) => {
    const isInterceptedUrl = intercepted.some((url) => httpRequest.url.includes(url));
    if (isInterceptedUrl) {
      return addAuthenticationHeader(tokenGenerator, httpRequest).pipe(concatMap((request) => next(request)));
    }
    return next(httpRequest);
  };
